import { useState, useEffect } from "react";
import { Modal, Table } from "react-bootstrap";
import ActionButton from "components/Buttons/ActionButton";
import QRScanner from "utils/QRScanner";
import { toast } from "react-toastify";
import CancelBackButton from "components/Buttons/CancelBackButton";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";

interface Bag {
  inventory_item_id: string | number;
  bag_no: string;
  available_quantity: number;
  issue_quantity: number;
  status?: "verified" | "not_verified" | null;
}

interface ViewBagsModalProps {
  show: boolean;
  handleClose: () => void;
  bags: Bag[];
  itemIndex: number | null;
  verifiedBags: number[];
  onBagsVerified: (
    itemIndex: number,
    verifiedBagIndices: number[],
    updatedBags: Bag[]
  ) => void;
}

const ViewBagsModal: React.FC<ViewBagsModalProps> = ({
  show,
  handleClose,
  bags = [],
  itemIndex,
  verifiedBags = [],
  onBagsVerified,
}) => {
  const [bagStatuses, setBagStatuses] = useState<
    Record<number, "verified" | "not_verified" | null>
  >({});
  const [showScanner, setShowScanner] = useState(false);
  const [currentBagIndex, setCurrentBagIndex] = useState<number | null>(null);

  // Initialize bag statuses from bags array and verifiedBags prop
  useEffect(() => {
    const initialStatuses: Record<number, "verified" | "not_verified" | null> =
      {};

    // First, set statuses from bags array if they exist
    bags.forEach((bag, idx) => {
      if (bag.status) {
        initialStatuses[idx] = bag.status;
      }
    });

    // Then, set verified statuses from verifiedBags prop
    if (verifiedBags.length > 0) {
      verifiedBags.forEach((bagIdx) => {
        initialStatuses[bagIdx] = "verified";
      });
    }

    setBagStatuses(initialStatuses);
  }, [verifiedBags, show, bags]);

  const handleScanClick = (bagIndex: number) => {
    setCurrentBagIndex(bagIndex);
    setShowScanner(true);
  };

  const handleScanResult = (scannedValue: any) => {
    if (currentBagIndex === null) return;

    const bag = bags[currentBagIndex];
    let scannedId: string | number;

    // Extract ID from scanned value
    if (typeof scannedValue === "string" || typeof scannedValue === "number") {
      // Direct string or number value
      scannedId = scannedValue;
    } else if (typeof scannedValue === "object" && scannedValue !== null) {
      // Try to get ID from object (could be inventory_item_id, id, etc.)
      scannedId =
        scannedValue.inventory_item_id || scannedValue.id || scannedValue;
    } else {
      scannedId = scannedValue;
    }

    // Convert both to strings for comparison (trim whitespace)
    const bagInventoryId = String(bag.inventory_item_id).trim();
    const scannedIdStr = String(scannedId).trim();

    // Compare scanned ID with inventory_item_id
    const newStatus: "verified" | "not_verified" =
      bagInventoryId === scannedIdStr ? "verified" : "not_verified";

    setBagStatuses((prev) => ({
      ...prev,
      [currentBagIndex]: newStatus,
    }));

    // Update the bag in the bags array with status
    if (newStatus === "verified") {
      toast.success("Bag verified successfully!");
    } else {
      toast.error(
        `Bag verification failed!`
        // `Bag verification failed! Expected: ${bagInventoryId}, Scanned: ${scannedIdStr}`
      );
    }

    setShowScanner(false);
    setCurrentBagIndex(null);
  };

  const handleDoneClick = () => {
    if (itemIndex === null) {
      toast.error("Invalid item index");
      return;
    }

    // Check if all bags are verified
    const allBagsVerified = bags.every((bag, idx) => {
      const status = bagStatuses[idx] || bag.status;
      return status === "verified";
    });

    if (!allBagsVerified) {
      toast.error("Please verify all bags before proceeding");
      return;
    }

    // Get all verified bag indices and update bags with status
    const updatedBags = bags.map((bag, idx) => {
      const status = bagStatuses[idx] || bag.status;
      return {
        ...bag,
        status: status || null,
      };
    });

    const verifiedBagIndices = updatedBags
      .map((bag, idx) => {
        return bag.status === "verified" ? idx : null;
      })
      .filter((idx) => idx !== null) as number[];

    // Call the callback to update parent state with verified indices and updated bags
    onBagsVerified(itemIndex, verifiedBagIndices, updatedBags);
    toast.success("All bags verified successfully!");
    handleClose();
  };

  const getStatusForBadge = (
    status: "verified" | "not_verified" | null | undefined
  ): string => {
    if (!status) {
      return "Not Scanned";
    }
    if (status === "verified") {
      return "Verified";
    }
    if (status === "not_verified") {
      return "Not Verified";
    }
    return "Not Scanned";
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header className="border-0">
          <Modal.Title className="fw-bold">Added Bags</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>
        <hr />
        <Modal.Body>
          {bags.length === 0 ? (
            <div className="text-center py-4 text-muted">No bags added.</div>
          ) : (
            <Table responsive className="align-middle text-center">
              <thead className="table-light border-bottom">
                <tr>
                  <th style={{ minWidth: 70 }} className="py-3 px-3">
                    Sr No.
                  </th>
                  <th style={{ minWidth: 200 }} className="py-3 px-3">
                    Bag No.
                  </th>
                  {/* <th style={{ minWidth: 200 }} className="py-3 px-3">
                                        Available Quantity
                                    </th> */}
                  <th style={{ minWidth: 200 }} className="py-3 px-3">
                    Issued Quantity
                  </th>
                  <th style={{ minWidth: 150 }} className="py-3 px-3">
                    Status
                  </th>
                  <th style={{ minWidth: 130 }} className="py-3 px-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {bags.map((bag, idx) => (
                  <tr key={idx} className="border-bottom">
                    <td className="py-2 px-3">{idx + 1}</td>
                    <td className="py-2 px-3">{bag.bag_no || "-"}</td>
                    {/* <td className="py-2 px-3">{bag.available_quantity || "-"}</td> */}
                    <td className="py-2 px-3">{bag.issue_quantity || "-"}</td>
                    <td className="py-2 px-3">
                      <OrderStatusBadge
                        status={getStatusForBadge(
                          bagStatuses[idx] || bag.status || null
                        )}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <ActionButton
                        label="Scan"
                        icon="bi bi-qr-code-scan"
                        handleClick={() => handleScanClick(idx)}
                        variant="primary"
                        disabled={
                          (bagStatuses[idx] || bag.status) === "verified"
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <div className="w-100 d-flex justify-content-center gap-2">
            <CancelBackButton label="Cancel" onClick={handleClose} />
            <ActionButton
              label="Done"
              variant="primary"
              handleClick={handleDoneClick}
            />
          </div>
        </Modal.Footer>
      </Modal>

      <QRScanner
        show={showScanner}
        handleClose={() => {
          setShowScanner(false);
          setCurrentBagIndex(null);
        }}
        onResult={handleScanResult}
      />
    </>
  );
};

export default ViewBagsModal;
